<template>
  <aside
    v-if="store.isOpen"
    :id="REPORT_GRAPHS_DRAWER_ID"
    ref="pane"
    class="
      tw-relative
      tw-flex
      tw-h-full
      tw-max-w-full
      tw-shrink-0
      tw-flex-col
      tw-border
      tw-border-solid
      tw-border-sf-slate-lighter-1
      tw-bg-white
    "
    :class="paneClasses"
    :style="paneStyle"
    data-testid="report-graphs-drawer"
    :aria-label="I18n.t('report.drawer.title')"
  >
    <header
      class="
        tw-flex
        tw-h-12
        tw-shrink-0
        tw-items-center
        tw-justify-between
        tw-gap-4
        tw-border-x-0
        tw-border-b
        tw-border-t-0
        tw-border-solid
        tw-border-sf-slate-lighter-1
        tw-pl-5
        tw-pr-4
      "
    >
      <h2
        class="tw-m-0 tw-text-sm tw-font-semibold tw-text-sf-neutral-darker-1"
      >
        {{ I18n.t('report.drawer.title') }}
      </h2>
      <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-0.5">
        <SfButtonDs
          v-if="showReset"
          v-tooltip="I18n.t('report.drawer.reset.tooltip')"
          icon-only
          icon-before="rotate-left"
          variant="tertiary"
          size="sm"
          data-testid="report-graphs-drawer-reset"
          :aria-label="I18n.t('report.drawer.reset.tooltip')"
          @click="confirmReset"
        />
        <SfButtonDs
          v-tooltip="fullWidthTooltip"
          icon-only
          :icon-before="
            store.fullscreen
              ? 'down-left-and-up-right-to-center'
              : 'up-right-and-down-left-from-center'
          "
          variant="tertiary"
          size="sm"
          data-testid="report-graphs-drawer-fullscreen-toggle"
          :aria-label="fullWidthTooltip"
          :aria-pressed="store.fullscreen"
          @click="
            store.fullscreen ? store.closeFullscreen() : store.openFullscreen()
          "
        />
        <SfButtonDs
          v-tooltip="I18n.t('report.drawer.collapse')"
          icon-only
          icon-before="xmark"
          variant="tertiary"
          size="sm"
          data-testid="report-graphs-drawer-close"
          :aria-label="I18n.t('report.drawer.collapse')"
          @click="store.closeDrawer()"
        />
      </div>
    </header>
    <div
      class="
        tw-flex-1
        tw-min-h-0
        tw-overflow-y-auto
        tw-p-6
        tw-flex
        tw-flex-col
        tw-gap-4
      "
    >
      <div
        v-if="showMainGroup"
        class="
          tw-bg-sf-slate-subtle-1
          tw-rounded-lg
          tw-p-4
          tw-flex
          tw-flex-col
          tw-gap-4
        "
      >
        <PinnedGraphCard
          :chart-height="pinnedChartHeight"
          :read-only="readOnly"
        />
        <HighlightedMetricsSection
          :fullscreen="store.fullscreen"
          :read-only="readOnly"
        />
      </div>
      <InsightMetricsSection
        :fullscreen="store.fullscreen"
        :read-only="readOnly"
      />
    </div>
    <ReportPaneResizer :pane="pane" />
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRef } from 'vue'

import PinnedGraphCard from '@app/application/report/components/graphs-drawer/pinned-graph-card.vue'
import HighlightedMetricsSection from '@app/application/report/components/graphs-drawer/highlighted-metrics-section.vue'
import InsightMetricsSection from '@app/application/report/components/graphs-drawer/insight-metrics-section.vue'
import ReportPaneResizer from '@app/application/report/components/report-pane-resizer.vue'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import { useReportPaneToggleHotkey } from '@app/application/report/composables/use-report-pane-toggle-hotkey'
import {
  PINNED_CHART_HEIGHT_DEFAULT,
  PINNED_CHART_HEIGHT_FULLSCREEN,
  REPORT_GRAPHS_DRAWER_DEFAULT_WIDTH_PX,
  REPORT_GRAPHS_DRAWER_ID,
  REPORT_GRAPHS_DRAWER_MIN_WIDTH_PX,
} from '@app/application/report/constants'
import { SfButtonDs } from '@silverfin/ui-components'

import useBodyClass from '@app/shared/vue/composables/use-body-class'
import useI18n from '@app/shared/vue/composables/use-I18n'

// `report-graphs-drawer-fullscreen` on <body> drives CSS that hides the rest
// of the report row while the sidebar takes the full width. See
// `report-graphs-drawer-fullscreen.scss`.
const FULLSCREEN_BODY_CLASS = 'report-graphs-drawer-fullscreen'

const I18n = useI18n()
const store = useReportGraphsDrawer()

useReportPaneToggleHotkey()

// In presentation mode the drawer is read-only: every section hides its edit
// controls when `readOnly` is set.
const readOnly = window.Silverfin.presentationMode ?? false

const pane = ref<HTMLElement | null>(null)

// The aside is a flex item of the report row, sized by the store, and it
// becomes the row's only visible item at full width.
const paneClasses = computed(() =>
  store.fullscreen ? 'tw-flex-1 tw-min-w-0' : null
)

// A stored percent of a narrow row would squeeze the metric pills, so the
// pixel floor wins over it; `tw-max-w-full` still caps it on a narrower row.
const paneStyle = computed(() => {
  if (store.fullscreen) return undefined
  return {
    width:
      store.widthPct === null
        ? `${REPORT_GRAPHS_DRAWER_DEFAULT_WIDTH_PX}px`
        : `${store.widthPct}%`,
    minWidth: `${REPORT_GRAPHS_DRAWER_MIN_WIDTH_PX}px`,
  }
})

const pinnedChartHeight = computed(() =>
  store.fullscreen
    ? PINNED_CHART_HEIGHT_FULLSCREEN
    : PINNED_CHART_HEIGHT_DEFAULT
)

const fullWidthTooltip = computed(() =>
  store.fullscreen
    ? I18n.t('report.drawer.full_width.close')
    : I18n.t('report.drawer.full_width.open')
)

const showMainGroup = computed(
  () =>
    store.pinned !== null ||
    store.highlightedCount > 0 ||
    (!readOnly && !store.setupLocked)
)

const showReset = computed(
  () => !readOnly && !store.setupLocked && store.resettable
)

function confirmReset() {
  window.sfConfirm({
    question: I18n.t('report.drawer.reset.confirm.title'),
    message: I18n.t('report.drawer.reset.confirm.body'),
    confirmLabel: I18n.t('report.drawer.reset.confirm.ok'),
    onConfirm: () => store.resetSetup(),
  })
}

useBodyClass(FULLSCREEN_BODY_CLASS, toRef(store, 'fullscreen'))

onMounted(() => {
  // Presentation mode auto-opens the drawer regardless of any saved preference:
  // the charts are what the audience came to see.
  if (readOnly) store.openDrawerWithoutPersisting()
})
</script>
